import {
  Box,
  ButtonIcon,
  Cell,
  type ColumnConfig,
  Link,
  Flex,
  SearchField,
  type SortDescriptor,
  Table,
  Tag,
  TagGroup,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import type { TeamProjectDto } from '@internal/scorecards-common';
import {
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';

import { useMemo, useState } from 'react';

import { RiInformationLine } from '@remixicon/react';
import { PassFailBar } from '../SegmentedBar/SegmentedBar';
import { matchesSearch, passPct } from './projectFilters';
import styles from './ProjectsTable.module.css';
import { useRouteRef } from '@backstage/core-plugin-api';

type ProjectRow = TeamProjectDto & { id: string };

const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const relativeTimeFormat = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'always',
});

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function formatAge(iso: string, now = Date.now()): string {
  const ageMs = Math.max(now - new Date(iso).getTime(), 0);
  if (ageMs < HOUR_MS)
    return relativeTimeFormat.format(-Math.round(ageMs / MINUTE_MS), 'minute');
  if (ageMs < DAY_MS)
    return relativeTimeFormat.format(-Math.round(ageMs / HOUR_MS), 'hour');
  return relativeTimeFormat.format(-Math.round(ageMs / DAY_MS), 'day');
}

const LastChecked = ({ project }: { project: TeamProjectDto }) => (
  <Flex
    direction="column"
    gap="0.5"
    title={project.stale ? 'Stale' : undefined}
  >
    <Text
      as="span"
      variant="body-small"
      className={project.stale ? styles.stale : undefined}
    >
      {dateTimeFormat.format(new Date(project.lastCheckedAt))}
    </Text>
    <Text
      as="span"
      variant="body-small"
      color={project.stale ? undefined : 'secondary'}
      className={project.stale ? styles.stale : undefined}
    >
      {formatAge(project.lastCheckedAt)}
      {project.stale && <span className={styles.srOnly}> (Stale)</span>}
    </Text>
  </Flex>
);

interface CategoryFilterItem {
  id: string;
  name: string;
}

interface ProjectsTableProps {
  projects: TeamProjectDto[];
  categoryFilters?: Set<string>;
  categoryFilterItems?: CategoryFilterItem[];
  onRemoveCategoryFilter?: (id: string) => void;
  onClearAllCategoryFilters?: () => void;
}

export const ProjectsTable = ({
  projects,
  categoryFilters,
  categoryFilterItems,
  onRemoveCategoryFilter,
  onClearAllCategoryFilters,
}: ProjectsTableProps) => {
  const entityRoute = useRouteRef(entityRouteRef);
  const [query, setQuery] = useState('');
  const [columnSort, setColumnSort] = useState<SortDescriptor | null>(null);

  const rows: ProjectRow[] = useMemo(() => {
    let filtered = projects.filter(p => matchesSearch(p, query));
    if (categoryFilters && categoryFilters.size > 0) {
      filtered = filtered.filter(p =>
        p.failingChecks.some(
          c => c.trackId !== null && categoryFilters.has(c.trackId!),
        ),
      );
    }
    if (columnSort) {
      filtered = [...filtered].sort((a, b) => {
        let cmp = 0;
        if (columnSort.column === 'project') {
          cmp = a.projectName.localeCompare(b.projectName);
        } else if (columnSort.column === 'progress') {
          cmp = passPct(a) - passPct(b);
        } else if (columnSort.column === 'lastChecked') {
          cmp = a.lastCheckedAt.localeCompare(b.lastCheckedAt);
        }
        return columnSort.direction === 'descending' ? -cmp : cmp;
      });
    }
    return filtered.map(p => ({ ...p, id: p.projectPath }));
  }, [projects, query, columnSort, categoryFilters]);

  const columnConfig: ColumnConfig<ProjectRow>[] = useMemo(() => {
    const entityHref = (ref: string) => {
      try {
        return `${entityRoute(entityRouteParams(ref))}/scorecards`;
      } catch {
        return undefined;
      }
    };
    return [
      {
        id: 'project',
        label: 'Project',
        isRowHeader: true,
        isSortable: true,
        cell: row => {
          const href = entityHref(row.catalogEntityRef ?? row.entityRef);
          return (
            <Cell>
              <Flex align="center" gap="1.5">
                {href ? (
                  <Link
                    href={href}
                    variant="body-medium"
                    truncate
                    title={row.projectName}
                  >
                    {row.projectName}
                  </Link>
                ) : (
                  <Text
                    as="p"
                    variant="body-medium"
                    truncate
                    title={row.projectName}
                  >
                    {row.projectName}
                  </Text>
                )}
                {!row.catalogEntityRef && (
                  <TooltipTrigger>
                    <ButtonIcon
                      aria-label="This project has not yet been onboarded to the Infinity Portal"
                      variant="tertiary"
                      size="small"
                      icon={<RiInformationLine />}
                      className={styles.infoIcon}
                    />
                    <Tooltip>
                      This project has not yet been onboarded to the Infinity
                      Portal
                    </Tooltip>
                  </TooltipTrigger>
                )}
              </Flex>
            </Cell>
          );
        },
      },
      {
        id: 'progress',
        label: 'Progress (passed / total)',
        isSortable: true,
        cell: row => (
          <Cell>
            <Flex direction="column" gap="1.5">
              <Flex align="baseline" gap="1">
                <Text as="span" variant="body-small" weight="bold">
                  {passPct(row)}%
                </Text>
                <Text as="span" variant="body-small" color="secondary">
                  {row.passed} / {row.total}
                </Text>
              </Flex>
              <PassFailBar
                passing={row.passed}
                failing={row.total - row.passed}
                total={row.total}
              />
            </Flex>
          </Cell>
        ),
      },
      {
        id: 'lastChecked',
        label: 'Last checked',
        isSortable: true,
        cell: row => (
          <Cell>
            <LastChecked project={row} />
          </Cell>
        ),
      },
    ];
  }, [entityRoute]);

  return (
    <section aria-labelledby="team-projects-heading">
      <Flex direction="column" gap="4">
        <Flex gap="3" justify="between" align="center">
          <Text as="h2" id="team-projects-heading" variant="title-small">
            Projects
          </Text>
          <Flex align="center" gap="2">
            {categoryFilterItems && categoryFilterItems.length > 0 && (
              <Flex align="center" gap="2">
                <TagGroup
                  aria-label="Active category filters"
                  onRemove={keys => {
                    keys.forEach(k => onRemoveCategoryFilter?.(String(k)));
                  }}
                >
                  {categoryFilterItems.map(item => (
                    <Tag
                      key={item.id}
                      id={item.id}
                      size="small"
                      textValue={item.name}
                    >
                      {item.name}
                    </Tag>
                  ))}
                </TagGroup>
                {categoryFilterItems.length > 1 && (
                  <button
                    type="button"
                    className={styles.clearAllFilters}
                    onClick={onClearAllCategoryFilters}
                  >
                    Clear all
                  </button>
                )}
              </Flex>
            )}
            <Box>
              <SearchField
                value={query}
                onChange={setQuery}
                aria-label="Search projects"
                placeholder="Search projects"
              />
            </Box>
          </Flex>
        </Flex>
        <Table
          columnConfig={columnConfig}
          data={rows}
          pagination={{ type: 'none' }}
          sort={{ descriptor: columnSort, onSortChange: setColumnSort }}
          emptyState={
            <Text as="p" color="secondary">
              {categoryFilters && categoryFilters.size > 0
                ? 'No failing projects match the selected categories.'
                : 'No projects match your search.'}
            </Text>
          }
        />
      </Flex>
    </section>
  );
};
